import { FilterManager, KlevuDomEvents, KlevuFilterType } from "@klevu/core";
import { KlevuFilter } from "~/modules/klevu/types";
import { KLEVU_CUSTOM_FILTER_NAMES } from "~/modules/klevu/variables/KlevuCustomFilterNames";
import { KLEVU_CUSTOM_FILTER_FILTER_SORTING } from "~/modules/klevu/variables/KlevuCustomFilterFilterSorting";

/**
 * `FilterManager` joins a filter's selected values with a comma and splits on a
 * comma when reading them back, and `URLSearchParams` decodes %2C before that
 * split runs. A category whose own name contains a comma ("Käeauguga kastid,
 * kandekastid") therefore came back as two values that match no option, leaving
 * the page with no results (PKNEW-1462). Escaping the comma inside a value
 * keeps the separator unambiguous; a value without one is written exactly as
 * before, so existing filter links keep working.
 */
function escapeValue(value: string): string {
  return value.replace(/%/g, "%25").replace(/,/g, "%2C");
}

function unescapeValue(value: string): string {
  return value.replace(/%2C/g, ",").replace(/%25/g, "%");
}

export class ZaprooKlevuFilterManager extends FilterManager {
  // Method to initialize filters from list of filters
  override initFromListFilters(filters: Array<KlevuFilter>) {
    // forEach loop to iterate each element in filters
    filters.forEach((el) => {
      // If condition to check if el.key exists in KLEVU_CUSTOM_FILTER_NAMES
      if (KLEVU_CUSTOM_FILTER_NAMES[el.key]) {
        // If true, assign the corresponding value to el.label
        el.label = KLEVU_CUSTOM_FILTER_NAMES[el.key];
      }
    });

    // Sort the filters based on their indices in KLEVU_CUSTOM_FILTER_FILTER_SORTING
    filters.sort((x, y) => {
      const xCustomIndex = KLEVU_CUSTOM_FILTER_FILTER_SORTING.indexOf(x.key);
      const yCustomIndex = KLEVU_CUSTOM_FILTER_FILTER_SORTING.indexOf(y.key);

      if (xCustomIndex !== -1 && yCustomIndex !== -1) {
        return xCustomIndex - yCustomIndex;
      } else if (xCustomIndex !== -1) {
        return -1;
      }

      return 0;
    });

    // Call the parent class function initFromListFilters and pass filters array as argument
    super.initFromListFilters(filters);
  }

  /**
   * Copy human-readable labels from a Klevu list-filters response onto this
   * manager's existing filters, applying the same custom-name overrides as
   * `initFromListFilters` but WITHOUT replacing the current filters/selection.
   * Used to re-label a manager (e.g. the committed/read manager) that is built
   * only from URL params and therefore carries raw attribute codes as labels.
   */
  relabelFromListFilters(listFilters: Array<KlevuFilter>): void {
    const labelByKey = new Map(
      listFilters.map((el) => [
        el.key,
        KLEVU_CUSTOM_FILTER_NAMES[el.key] ?? el.label,
      ]),
    );

    this.filters.forEach((filter) => {
      const label = labelByKey.get(filter.key);
      if (label) {
        filter.label = label;
      }
    });
  }

  // Method to reset all filters
  reset(): void {
    // forEach loop to iterate each element in filters, and reset each filter
    this.filters.forEach((el) => {
      this.resetFilter(el);
    });
  }

  // Method to reset a given filter - passed filter object as argument
  resetFilter(filter: {
    key: string;
    type: KlevuFilterType;
    value?: string;
  }): void {
    // Switch cases based on filter types
    switch (true) {
      case filter.type === KlevuFilterType.Slider:
        // If filter type is 'Slider', update the slide with empty key and value
        this.updateSlide(filter.key, "" as any, "" as any);
        break;
      case filter.type === KlevuFilterType.Options:
      case filter.type === KlevuFilterType.Rating:
        // If filter type is 'Options' or 'Rating'
        if (filter.value) {
          // If there is a value provided, deselect that option
          this.deselectOption(filter.key, filter.value);
        } else {
          // If no value is provided, clear all option selections for that key
          this.clearOptionSelections(filter.key);
        }
        break;
    }
  }

  /** Escape commas inside option values; the base class handles the rest. */
  override toURLParams(searchParams?: URLSearchParams): string {
    // Let the base fill/prune `params` in place (it writes into the object it
    // is given), then overwrite the option values with escaped ones. Rewriting
    // the same object — rather than re-parsing the base's serialised output —
    // keeps a caller-supplied `searchParams` in sync with the returned string.
    const params = searchParams ?? new URLSearchParams();
    super.toURLParams(params);

    for (const filter of this.filters) {
      if (!("options" in filter)) continue;

      const selected = filter.options.filter((o) => o.selected);

      if (selected.length) {
        params.set(
          `o_${filter.key}`,
          selected.map((o) => escapeValue(o.value)).join(","),
        );
      }
    }

    return params.toString();
  }

  /** Counterpart to {@link toURLParams}: unescape each value after the split. */
  override readFromURLParams(params: URLSearchParams): void {
    this.clearOptionSelections(undefined, false);

    for (const [key, value] of params.entries()) {
      const filterKey = key.substring(2);

      if (key.startsWith("o")) {
        value
          .split(",")
          .forEach((v) =>
            this.selectOption(filterKey, unescapeValue(v), false),
          );
      } else if (key.startsWith("s")) {
        const [min, max] = value.split("-");
        this.updateSlide(filterKey, parseFloat(min), parseFloat(max), false);
      }
    }

    // The base implementation ends with this event; keep firing it so listeners
    // still see filters restored from the URL.
    if (typeof document !== "undefined") {
      document.dispatchEvent(
        new CustomEvent(KlevuDomEvents.FiltersApplied, {
          detail: { filters: this.filters },
        }),
      );
    }
  }

  // Method to check if any filter is selected, returns boolean
  hasSelected(key: string): boolean {
    // Checks if key exists in selectedFilters
    return this.selectedFilters().some((s) => s.key === key);
  }
}
